import { useMemo, useCallback, useRef, useState } from 'react';
import { AsyncPaginate, LoadOptions } from 'react-select-async-paginate';
import {
  components,
  OptionProps,
  GroupBase,
  DropdownIndicatorProps,
  ClearIndicatorProps,
  NoticeProps,
  Props as SelectProps,
} from 'react-select';
import { CheckIcon, ChevronDown, Loader, PackageOpen, X } from 'lucide-react';
import { Skeleton } from '@/components/ui/Skeleton.tsx';
import Option from '@/components/form/types/Option.ts';
import { getSelectStyles } from '@/components/form/_primitives/primitiveSelectStyles.ts';
import {
  optionAriaLabel,
  renderOptionLabel,
} from '@/components/form/_primitives/optionLabels.ts';
import { useMobileSelectMenu } from '@/components/form/_primitives/mobileSelectMenu.ts';

type PrimitiveAsyncSelectProps = {
  value?: string | object;
  onChange?: (value: string | object | undefined) => void;
  loadOptions: LoadOptions<Option, GroupBase<Option>, { page: number }>;
  error?: string;
  id?: string;
  pageSize?: number;
  initialOption?: Option;
  enabled?: boolean;
  cacheUniqs?: unknown[];
} & Omit<
  SelectProps<Option, false>,
  'value' | 'onChange' | 'options' | 'isMulti' | 'loadOptions' | 'pageSize'
>;

// Stała referencja: `cacheUniqs` trafia do tablicy zależności efektu w
// react-select-async-paginate, więc nowa tablica przy każdym renderze
// przeładowywałaby opcje w kółko.
const EMPTY_CACHE_UNIQS: unknown[] = [];

const PrimitiveAsyncSelect = ({
  value,
  onChange,
  loadOptions,
  error,
  id,
  pageSize = 20,
  initialOption,
  enabled = true,
  cacheUniqs = EMPTY_CACHE_UNIQS,
  ...props
}: PrimitiveAsyncSelectProps) => {
  // Lista przychodzi z backendu strona po stronie, więc arkusz zawsze
  // dostaje pole wyszukiwania — bez niego nie ma jak dojść do dalszych pozycji.
  const mobileMenu = useMobileSelectMenu({
    title:
      props.mobileMenuTitle ??
      (typeof props.placeholder === 'string' ? props.placeholder : undefined),
    searchable: true,
  });
  const isMobileMenu = mobileMenu !== null;
  const styles = useMemo(
    () =>
      getSelectStyles<false>(error, 'default', { mobileMenu: isMobileMenu }),
    [error, isMobileMenu],
  );
  const loadedOptionsRef = useRef<Option[]>([]);
  const selectedOptionRef = useRef<Option | null>(null);
  const [optionFoundTrigger, setOptionFoundTrigger] = useState(0);

  // Wrap loadOptions to track all loaded options
  const wrappedLoadOptions: LoadOptions<
    Option,
    GroupBase<Option>,
    { page: number }
  > = useCallback(
    async (search, loadedOptions, additional) => {
      const result = await loadOptions(search, loadedOptions, additional);

      // Store loaded options for value lookup
      if (additional?.page === 0) {
        loadedOptionsRef.current = result.options;
      } else {
        loadedOptionsRef.current = [
          ...loadedOptionsRef.current,
          ...result.options,
        ];
      }

      // If we have a value, try to find and store the matching option
      if (value && !selectedOptionRef.current) {
        const found = loadedOptionsRef.current.find(
          (opt) => opt.value === value,
        );
        if (found) {
          selectedOptionRef.current = found;
          setOptionFoundTrigger((prev) => prev + 1);
        }
      }

      return result;
    },
    [loadOptions, value],
  );

  // Find the selected option from value
  const selectedValue = useMemo(() => {
    if (!value) {
      selectedOptionRef.current = null;
      return null;
    }

    // First check if we already have the selected option cached
    if (
      selectedOptionRef.current &&
      selectedOptionRef.current.value === value
    ) {
      return selectedOptionRef.current;
    }

    // Try to find in loaded options
    const found = loadedOptionsRef.current.find((opt) => opt.value === value);
    if (found) {
      selectedOptionRef.current = found;
      return found;
    }

    // Use initialOption if provided, otherwise fallback to empty label
    if (initialOption && initialOption.value === value) {
      selectedOptionRef.current = initialOption;
      return initialOption;
    }

    // Fallback: return option with empty label (will update when options load)
    return { value, label: '' } as Option;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, initialOption, optionFoundTrigger]);

  const closeMobileMenu = mobileMenu?.onSelect;
  const handleChange = useCallback(
    (selectedOption: Option | null) => {
      selectedOptionRef.current = selectedOption;
      onChange?.(selectedOption?.value);
      closeMobileMenu?.();
    },
    [onChange, closeMobileMenu],
  );

  return (
    <AsyncPaginate<Option, GroupBase<Option>, { page: number }, false>
      {...props}
      inputId={id ?? props.name}
      placeholder={props.placeholder ?? 'Wyszukaj...'}
      loadOptions={wrappedLoadOptions}
      // `defaultOptions` steruje też przeładowaniem po zmianie `cacheUniqs`:
      // dopóki select jest wyłączony, nie pobiera nic, a gdy zależny filtr się
      // wypełni, pierwsza strona doczytuje się bez wpisywania czegokolwiek.
      defaultOptions={enabled}
      additional={{ page: 0 }}
      debounceTimeout={300}
      classNamePrefix="custom-select"
      isSearchable
      isMulti={false}
      value={selectedValue}
      onChange={handleChange}
      getOptionLabel={props.getOptionLabel ?? optionAriaLabel}
      formatOptionLabel={props.formatOptionLabel ?? renderOptionLabel}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      menuShouldBlockScroll={true}
      cacheUniqs={cacheUniqs}
      pageSize={pageSize}
      components={{
        Option: DefaultCustomOption,
        DropdownIndicator: CustomDropdownIndicator,
        ClearIndicator: CustomClearIndicator,
        NoOptionsMessage: CustomNoOptions,
        LoadingIndicator: CustomLoadingIndicator,
        LoadingMessage: CustomLoadingMessage,
        ...props.components,
        ...mobileMenu?.components,
      }}
      styles={styles}
      {...mobileMenu?.selectProps}
    />
  );
};

const DefaultCustomOption = (
  props: OptionProps<Option, false, GroupBase<Option>>,
) => {
  return (
    <components.Option {...props}>
      <div className="flex items-center justify-between gap-2">
        <label className="text-sm leading-4">{props.children}</label>
        {props.isSelected && (
          <CheckIcon size={14} className="text-base-text" strokeWidth={3} />
        )}
      </div>
    </components.Option>
  );
};

const CustomDropdownIndicator = (
  props: DropdownIndicatorProps<Option, false>,
) => {
  const isLoading = props.selectProps.isLoading;

  return (
    <components.DropdownIndicator {...props}>
      <div className="flex items-center gap-1">
        {isLoading && (
          <Loader className="animate-spin text-neutral-900" size={16} />
        )}
        <ChevronDown
          className="text-neutral-600 hover:text-neutral-900"
          size={20}
          strokeWidth={2}
        />
      </div>
    </components.DropdownIndicator>
  );
};

// Hide default loading indicator (3 dots)
const CustomLoadingIndicator = () => null;

const CustomClearIndicator = (props: ClearIndicatorProps<Option, false>) => {
  return (
    <components.ClearIndicator {...props}>
      <X
        className="text-neutral-400 hover:cursor-pointer hover:text-neutral-600"
        size={16}
      />
    </components.ClearIndicator>
  );
};

const CustomNoOptions = (props: NoticeProps<Option, false>) => {
  return (
    <components.NoOptionsMessage {...props}>
      <div className="flex flex-row items-start gap-1 text-sm text-neutral-600">
        <PackageOpen className="size-5 min-w-5 text-neutral-400" />
        <p className="truncate">Brak wyników</p>
      </div>
    </components.NoOptionsMessage>
  );
};

const CustomLoadingMessage = (props: NoticeProps<Option, false>) => {
  return (
    <components.LoadingMessage {...props}>
      <div className="flex flex-col gap-1.5">
        {Array.from({ length: 14 }).map((_, index) => (
          <Skeleton key={index} className="h-5" />
        ))}
      </div>
    </components.LoadingMessage>
  );
};

export default PrimitiveAsyncSelect;
